import type { List } from '@design-system/components/Header/Menu/HeaderMenuNav.props'

/**
 * Manages cross-navigation links and category discovery for header navigation.
 *
 * @async
 * @function useCrossNavigationsLinks
 * @param {string} currentWebsite - The current website context
 * @param {string} path - The current navigation path
 *
 * @returns {Promise<Object>} An object containing the discovered category
 *
 * @description
 * This composable:
 * - Retrieves header configuration based on website and country/language
 * - Finds the current category in the menu structure
 * - Handles cross-brand homepage navigation
 *
 * @example
 * const { category } = await useCrossNavigationsLinks('mywebsite', '/en-US/path')
 */
export const useCrossNavigationsLinks = async (
  currentWebsite: string,
  path: string
) => {
  /**
   * Computed country and language from the current path.
   *
   * @type {ComputedRef<string>}
   */
  const countryAndLanguage = computed(() => path.split('/')[1])

  /**
   * Reference to the current website.
   *
   * @type {Ref<string>}
   */
  const refWebsite = ref(currentWebsite)

  /**
   * Current route information.
   *
   * @type {RouteLocationNormalizedLoaded}
   */
  const route = useRoute()

  /**
   * Retrieves header configuration for the current website and locale.
   *
   * @type {AsyncData<List[], Error>}
   */
  const nuxtApp = useNuxtApp()
  const { data: menuLists, error } = await useHeaderConfig(
    refWebsite,
    countryAndLanguage
  )
  console.log(error.value)

  /**
   * Recursively finds a category in the menu structure matching the current path.
   *
   * @function findCategory
   * @param {List | undefined} menuListItem - Current menu list item to search
   * @param {string} path - Current navigation path
   * @returns {List | null} The matching category or null
   *
   * @description
   * Traverses menu items and submenus to find a matching category
   * Checks both menu items and highlights for path match
   */
  const findCategory = (
    menuListItem: List | undefined,
    path: string
  ): List | null => {
    if (!menuListItem?.items?.length) return null

    const hasMenuItem = menuListItem.items.some(item => `${item.to}/` === path)
    const hasMenuHighlight = menuListItem?.highlights?.some(
      item => `${item.to}/` === path
    )
    if (hasMenuItem || hasMenuHighlight) return menuListItem

    for (const item of menuListItem.items) {
      const foundCategory = findCategory(item.subList, path)
      if (foundCategory) return foundCategory
    }

    return null
  }

  /**
   * Computes the current category based on the navigation path.
   *
   * @type {ComputedRef<{label: string | null, items: List[] | undefined}>}
   *
   * @description
   * - Returns an empty category for cross-brand homepage
   * - Finds the matching category in the menu structure
   * - Filters out menu items without a destination
   *
   * @returns the category object
   */
  const category = computed(() => {
    if (!menuLists.value?.[0]) {
      return { label: null, items: undefined }
    }

    const isCrossBrandHomepage =
      route.path === `/${countryAndLanguage.value}/${currentWebsite}/`

    if (isCrossBrandHomepage) return { label: '', items: [] }

    const foundCategory = findCategory(menuLists.value[0], route.path)

    return {
      label: foundCategory?.label ?? foundCategory?.parents?.[0] ?? '',
      items: foundCategory?.items.filter(item => item.to) ?? [],
    }
  })

  return {
    category,
  }
}
